// @loom/shared/brand — the product name a stranger sees.
//
// The name every surface reachable without a Loom login shows: store
// listings, the signed-out pages, the Gmail add-on. A placeholder until the
// final name; the internal name (code, logs, env vars, the signed-in UI, and
// everything GitLab shows) stays Loom.
//
// One exception, the UI public-name mode: on dev while `LOOM_UI_PUBLIC_NAME`
// is on (Figma's App Review signs in there), the signed-in UI and both agents'
// prompts say the public name too. The mode is one flag per process: the
// server sets it at boot from its config (`assembleApp`, `apps/server/src/app.ts`), the web
// when `/auth/me` answers `publicName: true` (`apps/web/src/auth/useAuth.ts`),
// the sandbox from its `LOOM_UI_PUBLIC_NAME` trigger variable. Off, every
// helper below returns its input unchanged.

export const PUBLIC_PRODUCT_NAME = "AI-Workbench";

/**
 * Fixed text with the exact word "Loom" swapped for the public name: "Loom",
 * "Loom's", "Loom’s" and "Loom-Konto" change; "Looms", "LoomConfig",
 * `.loom.yml`, `loom-coding/` and `mcp__loom__` do not, on purpose — a German
 * genitive or a file name needs a hand-written line, never a mechanical one.
 * Only for text Loom wrote: never for a user's words or a value filled in.
 * No lookbehind, so it runs on Safari 14.
 */
export const withPublicName = (text: string): string =>
  text.replace(/\bLoom\b/gu, PUBLIC_PRODUCT_NAME);

/** A fenced block (closed, or still streaming to the end) or an inline code
 *  span: code the model quoted, which the prose swap leaves alone. */
const CODE_PART = /(```[\s\S]*?(?:```|$)|`[^`\n]*`)/u;

/**
 * Model prose for display, under the public-name mode: the plain swap, plus
 * the German genitive the model writes ("Looms Issue-Editor" becomes
 * "AI-Workbench-Issue-Editor", a bare "Looms" the name). Code spans and fenced
 * blocks are left alone, so a `loom-confirm` fence's body is what Accept reads.
 * Display only: never stored, never sent back.
 */
export const withPublicNameProse = (text: string): string =>
  text
    .split(CODE_PART)
    .map((part, i) =>
      i % 2 === 1
        ? part
        : withPublicName(
            part
              .replace(/\bLooms\s+(?=\p{Lu})/gu, `${PUBLIC_PRODUCT_NAME}-`)
              .replace(/\bLooms\b/gu, PUBLIC_PRODUCT_NAME),
          ),
    )
    .join("");

let uiPublicNameOn = false;

/** Turn the public-name mode on or off for this process (or page). */
export const setUiPublicName = (on: boolean): void => {
  uiPublicNameOn = on;
};

/** Whether the public-name mode is on in this process (or page). */
export const uiPublicName = (): boolean => uiPublicNameOn;

/** Fixed text Loom shows or tells a model, swapped while the mode is on. */
export const uiText = (text: string): string =>
  uiPublicNameOn ? withPublicName(text) : text;

/** Model prose for display, swapped while the mode is on. */
export const uiProse = (text: string): string =>
  uiPublicNameOn ? withPublicNameProse(text) : text;

/**
 * The one rule both agents get while the mode is on, at the top of their
 * system prompt. It names how the agent refers to ITSELF and this tool, and
 * nothing else: on dev other people work on tickets about Loom, and the
 * project's and the ticket's own text must keep their words. The only "Loom"
 * a prompt carries under the mode.
 */
export const PUBLIC_NAME_RULE: string = [
  `PRODUCT NAME — in this deployment this tool is called ${PUBLIC_PRODUCT_NAME}.`,
  `When you refer to this tool itself (its workspace, its notes, its screens,`,
  `its issue editor), call it ${PUBLIC_PRODUCT_NAME}, in every language. Some`,
  `lines and tool results still use its internal name, Loom: never use that`,
  `name for this tool in anything you write to the user. This is only about`,
  `how you name this tool. Never change a name inside the project's own text,`,
  `an issue's title or body, or anything the user wrote.`,
].join("\n");
